<template>
	<div
		class="app-date-picker"
		v-on-clickaway="closePicker"
	>
		<div
			v-if="$slots.default"
			class="app-date-picker__label"
		>
			<span v-if="$attrs.required" class="require-mark">*</span>
			<slot></slot>
		</div>

		<div class="app-date-picker__wrapper">
			<div
				class="app-date-picker__toggle"
				:class="{
					'app-date-picker__toggle--disabled' : $attrs.disabled,
					'app-date-picker__toggle--error' : hasError
				}"
				@click="open = !open"
			>
				<AppIcon
					class="app-date-picker__icon"
					icon-name="calendar"
					icon-color="#778395"
				/>

				<input
					type="text"
					class="app-date-picker__input"
					v-bind="$attrs"
					@invalid="setInvalidMessage"
					:value="displayValue"
					@keydown.prevent
					@keypress.prevent
					@paste.prevent
					@input.prevent
				>
			</div>

			<AppCalendar
				v-if="open"
				:value="value"
				:min="min"
				:max="max"
				@input="selectDate($event)"
				class="app-date-picker__calendar"
				:class="{'app-date-picker__calendar--top' : getPositionReference().isTop,
						'app-date-picker__calendar--right' : getPositionReference().isRight}"
			/>
		</div>

		<p
			v-if="hasError"
			class="app-date-picker__error"
		>
			{{ invalidMessage || error }}
		</p>
	</div>
</template>

<script>
import AppCalendar from '@/components/AppCalendar.vue'
import AppIcon from '@/components/AppIcon.vue'
export default {
	inheritAttrs: false,

	props: {
		value: Date,
		min: Date,
		max: Date,
		error: String
	},

	data () {
		return {
			dateValue: null,
			open: false,
			invalidMessage: null
		}
	},

	computed: {
		hasError () {
			return this.error || this.invalidMessage
		},

		displayValue () {
			if (!this.value) return ''
			const year = this.value.getFullYear()
			const month = this.value.toLocaleString(undefined, { month: 'long' })
			const date = String(this.value.getDate()).padStart(2, '0')

			return `${month} - ${date} - ${year}`
		}
	},

	methods: {
		selectDate (value) {
			this.invalidMessage = null
			this.$emit('input', value)
		},

		setInvalidMessage (event) {
			event.preventDefault()
			if (event.target.validity.valueMissing) {
				this.invalidMessage = 'Fill up this field'
			} else {
				this.invalidMessage = event.target.validationMessage
			}
		},

		getPositionReference () {
			const elementPosition = this.$el.getBoundingClientRect()
			const isTop = window.innerHeight - elementPosition.top < elementPosition.top
			const isRight = window.innerWidth - elementPosition.left < elementPosition.left
			return { isTop, isRight }
		},

		closePicker () {
			this.open = false
		}
	},
	components: {
		AppCalendar,
		AppIcon
	}
}
</script>

<style lang="scss" scoped>
.app-date-picker {
	&__label {
		display: inline-block;
		font-size: 1em;
		margin-bottom: .5em;

		.require-mark {
			color: $danger;
		}
	}

	&__wrapper {
		margin: 0;
		padding: 0;
		position: relative;
		color: $default;
	}

	&__toggle {
		font-size: 1em;
		border-radius: 3px;
		padding: 5px 8px;
		border: 1px solid $default-light;
		display: flex;
		align-items: center;
		cursor: pointer;

		&--disabled {
			pointer-events: none;
			opacity: 0.5;
		}

		&--error {
			border-color: $danger;
		}
	}

	&__input {
		flex: 1 1 auto;
		font-size: 1em;
		line-height: 1em;
		margin-left: 8px;
		min-width: 0;
		border: none;
		outline: none;
		pointer-events: none;
		color: $default;
		background-color: #fff;
		caret-color: transparent;

		&::placeholder {
			color: $default-light;
		}
	}

	&__icon {
		width: 1em;
		flex-shrink: 0;
	}

	&:hover &__toggle {
		border-color: $primary;

		&--disabled {
			border-color: $default-light;
		}

		&--error {
			border-color: $danger;
		}
	}

	&__calendar {
		position: absolute;
		z-index: 999;
		top: 100%;
		left: 0;
		margin: 5px 0;

		&--top {
			top: auto;
			bottom: 100%;
		}

		&--right {
			left: auto;
			right: 0;
		}
	}

	&__error {
		color: $danger;
		margin-top: .5em;
		display: flex;
		justify-content: flex-end;
	}
}
</style>
